import { httpClient, PaginationParams } from '../core/http';
import {
  CreateCrewCategoryRequest,
  CreateCrewRequest,
  Crew,
  CrewCategory,
  UpdateCrewRequest,
} from '../types';
import { BaseService } from './BaseService';

export class CrewService extends BaseService<
  Crew,
  CreateCrewRequest,
  UpdateCrewRequest
> {
  constructor() {
    super('/v1/crews');
  }

  async getByOrganization(
    organizationId: number,
    includeHidden?: boolean,
    pagination?: PaginationParams,
    categoryId?: number
  ): Promise<Crew[]> {
    const params: Record<string, any> = {
      organization_id: organizationId,
      ...pagination,
    };
    if (categoryId !== undefined) {
      params.category_id = categoryId;
    }
    if (includeHidden !== undefined) {
      params.include_hidden = includeHidden;
    }
    const response = await this.getAll(params);
    return response.items;
  }

  async getActiveByOrganization(
    organizationId: number,
    pagination?: PaginationParams,
    categoryId?: number
  ): Promise<Crew[]> {
    const response = await this.getAll({
      organization_id: organizationId,
      is_active: true,
      category_id: categoryId,
      ...pagination,
    });
    return response.items;
  }

  async getCategories(organizationId: number): Promise<CrewCategory[]> {
    const response = await httpClient.get<CrewCategory[]>(`${this.basePath}/categories`, {
      params: { organization_id: organizationId },
    });
    return response.data || [];
  }

  async createCategory(payload: CreateCrewCategoryRequest): Promise<CrewCategory> {
    const response = await httpClient.post<CrewCategory>(`${this.basePath}/categories`, payload);
    return response.data;
  }

  async getByName(name: string, organizationId?: number): Promise<Crew | null> {
    try {
      const params: any = { name };
      if (organizationId) {
        params.organization_id = organizationId;
      }
      const response = await this.getAll(params);
      // Filtrar para encontrar exatamente o nome correspondente (case-sensitive)
      const exactMatch = response.items.find(crew => crew.name === name);
      return exactMatch || null;
    } catch (error) {
      return null;
    }
  }

  async getWithDetails(id: number): Promise<Crew> {
    const response = await httpClient.get<Crew>(
      `${this.basePath}/${id}?include_details=true`
    );
    return response.data;
  }

  async addAgent(crewId: number, agentId: number): Promise<void> {
    await httpClient.post(`${this.basePath}/${crewId}/agents`, {
      crew_id: crewId,
      agent_id: agentId,
    });
  }

  async removeAgent(crewId: number, agentId: number): Promise<void> {
    await httpClient.delete(`${this.basePath}/${crewId}/agents/${agentId}`);
  }

  async addTask(crewId: number, taskId: number): Promise<void> {
    await httpClient.post(`${this.basePath}/${crewId}/tasks`, {
      crew_id: crewId,
      task_id: taskId,
    });
  }

  async removeTask(crewId: number, taskId: number): Promise<void> {
    await httpClient.delete(`${this.basePath}/${crewId}/tasks/${taskId}`);
  }
}
